import { PaginationType } from '@api';
import { FileRoutesByFullPath } from '@providers';
import { UseNavigateResult } from '@tanstack/react-router';
import {
  useTableFiltering,
  useTablePagination,
  useTableSearch,
  useTableSorting,
} from './hooks';

type DefaultValuesType = {
  accessorOptions?: string[];
  defaultAccessor?: string;
  defaultDirection?: 'asc' | 'desc';
  pageSizeOptions?: number[];
  filterKeys?: readonly string[];
};

type UseManageTableOptionsType = {
  navigateFn: UseNavigateResult<keyof FileRoutesByFullPath>;
  searchParams: Record<string, unknown>;
  pagination?: PaginationType;
  config: DefaultValuesType;
  tableId?: string;
};

type TableArgumentsType = {
  sorting: ReturnType<typeof useTableSorting>;
  pagination: ReturnType<typeof useTablePagination>;
  search: {
    setSearchValue: (value: string) => void;
    searchValue: string | undefined;
  };
  filtering: ReturnType<typeof useTableFiltering>;
};

export type UseManageTableReturnType = {
  tableArguments: TableArgumentsType;
};

export const useManageTable = ({
  navigateFn,
  searchParams,
  pagination,
  config,
  tableId,
}: UseManageTableOptionsType): UseManageTableReturnType => {
  const sorting = useTableSorting({
    navigateFn,
    searchParams,
    config,
    tableId,
  });

  const paginationController = useTablePagination({
    navigateFn,
    search: searchParams,
    pagination,
    config,
    tableId,
  });

  const search = useTableSearch({
    searchParams: searchParams,
    navigateFn,
    tableId,
  });

  const filtering = useTableFiltering({
    navigateFn,
    searchParams,
    filterKeys: config.filterKeys,
    tableId,
  });

  const tableArguments = {
    sorting,
    pagination: paginationController,
    search,
    filtering,
  };

  return { tableArguments };
};
